import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, IconButton, Button } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { EntitlementAction } from "../../api/entitlementsApi";
import useEntitlements from "../../hooks/useEntitlements";
import { useOrganization } from "../../pages/facility/hooks/useOrganization";
import useSubscription from "../../pages/subscription/hooks/useSubscription";
import { formatDateLabel } from "../../pages/appointment/helpers/formatDate";

const SubscriptionStatusBanner = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription } = useSubscription();
  const { can, isOnboarded } = useEntitlements();
  const { organization } = useOrganization();
  const [dismissed, setDismissed] = useState(false);

  const orgId = organization?.id;
  const status = subscription?.status;
  // SubscriptionDetails is rendered on these routes — avoid duplicate cancel messaging there.
  const isSubscriptionDetailsRoute =
    location.pathname.startsWith("/subscription") ||
    location.pathname.startsWith("/organization");

  // Sync dismissal state with sessionStorage whenever org or status changes
  useEffect(() => {
    if (orgId && status) {
      const isDismissed = sessionStorage.getItem(
        `dismiss-sub-banner-${orgId}-${status}`,
      );
      setDismissed(!!isDismissed);
    } else {
      setDismissed(false);
    }
  }, [orgId, status]);

  const handleDismiss = () => {
    if (orgId && status) {
      sessionStorage.setItem(`dismiss-sub-banner-${orgId}-${status}`, "true");
      setDismissed(true);
    }
  };

  // Only show if onboarded to subscription and user has SUBSCRIPTION_MANAGEMENT entitlement
  if (
    dismissed ||
    !isOnboarded("subscription") ||
    !can(EntitlementAction.SUBSCRIPTION_MANAGEMENT)
  ) {
    return null;
  }

  // Display critical alert for expired trials
  if (status === "incomplete_expired") {
    return (
      <Box sx={{ mb: 2, mt: 2 }}>
        <Alert
          severity="error"
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 700 }}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          }
        >
          Your trial has ended. To continue using Medial Health features, please
          upgrade to a paid subscription.
          <Box sx={{ mt: 1 }}>
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              onClick={() => navigate("/subscription")}
              sx={{ fontWeight: 700 }}
            >
              Upgrade Now
            </Button>
          </Box>
        </Alert>
      </Box>
    );
  }

  // Display error for unpaid (restricted) accounts
  if (status === "unpaid") {
    return (
      <Box sx={{ mb: 2, mt: 2 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 1 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          Your account is currently RESTRICTED due to unpaid invoices (60+ days
          past due). Please update your payment method and clear the outstanding
          balance to restore access.
        </Alert>
      </Box>
    );
  }

  // Display warning for past_due accounts
  if (status === "past_due") {
    return (
      <Box sx={{ mb: 2, mt: 2 }}>
        <Alert
          severity="warning"
          sx={{ borderRadius: 2 }}
          action={
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                onClick={() => navigate("/subscription")}
                sx={{ fontWeight: 700 }}
              >
                Check Subscription
              </Button>
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={handleDismiss}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            </Box>
          }
        >
          Your subscription is currently PAST DUE. Please review your billing
          information and settle outstanding charges.
        </Alert>
      </Box>
    );
  }

  if (status === "cancellation_requested") {
    if (isSubscriptionDetailsRoute) {
      return null;
    }
    return (
      <Box sx={{ mb: 2, mt: 2 }}>
        <Alert
          severity="info"
          sx={{ borderRadius: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          Your cancellation request is pending. Our team will contact you to
          complete the process. You keep full access while the request is open.
        </Alert>
      </Box>
    );
  }

  if (status === "canceling") {
    if (isSubscriptionDetailsRoute) {
      return null;
    }
    const accessUntilLabel = formatDateLabel(
      subscription?.cancelEffectiveAt || subscription?.currentPeriodEnd,
    );
    return (
      <Box sx={{ mb: 2, mt: 2 }}>
        <Alert
          severity="warning"
          sx={{ borderRadius: 2 }}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          Your subscription is scheduled to end
          {accessUntilLabel ? ` on ${accessUntilLabel}` : ""}. You keep full
          access until then.
        </Alert>
      </Box>
    );
  }

  return null;
};

export default SubscriptionStatusBanner;
